// Segmented date input — three separate DD / MM / YYYY fields inside one
// bordered control, with an optional calendar overlay. Self-contained and
// DS-styled (.ar-dateinput-*). Plain vanilla JS, no framework.
//
// `mountDateInput(host, opts)` builds the field inside `host` and returns a
// controller. A hidden <input name=…> mirrors the committed ISO value
// (YYYY-MM-DD), so existing form code that reads `[name].value` keeps working.
//
// opts:
//   name      hidden input name (the form value carrier)
//   label     visible field label + group aria-label
//   value     initial value, ISO "YYYY-MM-DD"
//   min, max  ISO bounds — typed input rejects out-of-range, calendar disables
//   required  marks the field required (affects validate())
//   locale    BCP-47 tag → segment order (default "en-GB" → DD/MM/YYYY)
//   error     render the inline error slot (default true)
//   onChange  (iso|"") => void, fired when the committed value changes
//
// Controller: { element, getValue, setValue, validate, focus, destroy }

import { companyToday } from "../lib/company-day.js";
import { escapeHtml } from "../lib/format.js";

const SEG = {
  day:   { len: 2, label: "Day",   placeholder: "DD" },
  month: { len: 2, label: "Month", placeholder: "MM" },
  year:  { len: 4, label: "Year",  placeholder: "YYYY" },
};

const CAL_ICON = `<img class="ar-icon-24 ar-dateinput-cal-icon" src="/src/assets/icons/Icon=calendar-blue.svg" alt="" aria-hidden="true" />`;

function pad(v, len) { return String(v).padStart(len, "0"); }

// Segment order from the locale (e.g. en-US → month/day/year). Default DD/MM/YYYY.
function segmentOrder(locale) {
  try {
    const parts = new Intl.DateTimeFormat(locale || "en-GB").formatToParts(new Date(2001, 11, 23));
    const order = parts.filter((p) => p.type === "day" || p.type === "month" || p.type === "year").map((p) => p.type);
    if (order.length === 3) return order;
  } catch { /* fall through */ }
  return ["day", "month", "year"];
}

// Days in a month. Year empty → assume a leap year (2000) so Feb 29 isn't
// rejected before the year is typed; the combined check catches it later.
function daysInMonth(month, year) {
  const m = Number(month);
  if (!m || m < 1 || m > 12) return 31;
  const y = Number(year) || 2000;
  return new Date(y, m, 0).getDate();
}

function formatHuman(iso, locale) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  if (!m) return iso;
  try {
    return new Intl.DateTimeFormat(locale || "en-GB", { day: "numeric", month: "long", year: "numeric" })
      .format(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  } catch { return iso; }
}

function validateParts({ day, month, year }, { min, max, required, locale }) {
  if (!day && !month && !year) {
    return required ? { ok: false, iso: "", message: "Enter a date." } : { ok: true, iso: "" };
  }
  if (!day || !month || year.length < 4) {
    return { ok: false, iso: "", message: "Enter a full date." };
  }
  const d = Number(day), mo = Number(month), y = Number(year);
  if (mo < 1 || mo > 12) return { ok: false, iso: "", message: "Enter a month between 1 and 12." };
  if (y < 1900 || y > 2100) return { ok: false, iso: "", message: "Enter a year between 1900 and 2100." };
  const dim = new Date(y, mo, 0).getDate();
  if (d < 1 || d > dim) {
    const monthName = new Intl.DateTimeFormat(locale || "en-GB", { month: "long" }).format(new Date(y, mo - 1, 1));
    return { ok: false, iso: "", message: `${monthName} ${y} only has ${dim} days.` };
  }
  const iso = `${pad(y, 4)}-${pad(mo, 2)}-${pad(d, 2)}`;
  if (min && iso < min) return { ok: false, iso: "", message: `Choose a date on or after ${formatHuman(min, locale)}.` };
  if (max && iso > max) return { ok: false, iso: "", message: `Choose a date on or before ${formatHuman(max, locale)}.` };
  return { ok: true, iso };
}

// Parse a pasted date string into {day, month, year}, disambiguating by value
// (a part > 12 must be the day) and falling back to the locale order.
function parsePastedDate(text, order) {
  let groups = text.match(/\d+/g) || [];
  if (groups.length === 1 && groups[0].length === 8) {
    // Contiguous 8 digits: try YYYYMMDD, else DD/MM/YYYY-style per order.
    const s = groups[0];
    groups = /^(19|20)\d{6}$/.test(s)
      ? [s.slice(0, 4), s.slice(4, 6), s.slice(6, 8)]
      : [s.slice(0, 2), s.slice(2, 4), s.slice(4, 8)];
  }
  if (groups.length < 3) return null;

  const yearIdx = groups.findIndex((g) => g.length === 4);
  if (yearIdx === -1) return null;
  const year = Number(groups[yearIdx]);
  const rest = groups.filter((_, i) => i !== yearIdx).map(Number);
  if (rest.length < 2) return null;

  const [p1, p2] = rest;
  let day, month;
  if (p1 > 12 && p2 <= 12) { day = p1; month = p2; }
  else if (p2 > 12 && p1 <= 12) { day = p2; month = p1; }
  else {
    const nonYear = order.filter((s) => s !== "year");
    if (nonYear[0] === "day") { day = p1; month = p2; } else { month = p1; day = p2; }
  }
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > daysInMonth(month, year)) return null;
  if (year < 1900 || year > 2100) return null;
  return { day, month, year };
}

export function mountDateInput(host, {
  name = "",
  label = "Date",
  value = "",
  min = "",
  max = "",
  required = false,
  locale = "en-GB",
  hint = "",
  error = true,
  // For a compact row whose fields are led by placeholders rather than labels
  // (the instalment editor). The label is still rendered and still read aloud
  // — it is taken out of the visual flow, not out of the accessibility tree.
  labelHidden = false,
  onChange = null,
} = {}) {
  const order = segmentOrder(locale);
  const fieldKey = name || "date";
  const segs = { day: "", month: "", year: "" };
  let lastCommitted = null;

  // ── Markup ────────────────────────────────────────────────────────────────
  host.classList.add("ar-input-spec");
  // No label above the control means the spacing measured from one is wrong —
  // see .ar-input-spec-compact in system.css.
  if (labelHidden) host.classList.add("ar-input-spec-compact");
  host.innerHTML = `
    <span class="ar-input-spec-label${labelHidden ? " ar-input-spec-label-hidden" : ""}" data-field-label="${escapeHtml(fieldKey)}">${escapeHtml(label)}</span>
    ${hint ? `<p class="ar-input-spec-hint">${escapeHtml(hint)}</p>` : ""}
    <div class="ar-input-spec-field-wrap">
      <div class="ar-dateinput" role="group" aria-label="${escapeHtml(label)}">
        <div class="ar-dateinput-fields">
          ${order.map((seg, i) => {
            const m = SEG[seg];
            const sep = i < order.length - 1 ? `<span class="ar-dateinput-sep" aria-hidden="true">/</span>` : "";
            return `<input class="ar-dateinput-seg" data-seg="${seg}" type="text" inputmode="numeric"
                      autocomplete="off" autocorrect="off" spellcheck="false"
                      maxlength="${m.len}" aria-label="${m.label}" placeholder="${m.placeholder}" />${sep}`;
          }).join("")}
        </div>
        <button class="ar-dateinput-cal" type="button" aria-label="Open calendar" aria-haspopup="dialog" aria-expanded="false">${CAL_ICON}</button>
        <input type="hidden"${name ? ` name="${escapeHtml(name)}"` : ""} />
      </div>
    </div>
    ${error ? `<p class="ar-input-spec-error-text" data-field-error="${escapeHtml(fieldKey)}" role="status" aria-live="polite" hidden></p>` : ""}
    <p class="ar-input-spec-note-text" data-field-note="${escapeHtml(fieldKey)}" role="status" aria-live="polite" hidden></p>
  `;

  const root = host.querySelector(".ar-dateinput");
  const hidden = host.querySelector('input[type="hidden"]');
  const errorEl = host.querySelector("[data-field-error]");
  const noteEl = host.querySelector("[data-field-note]");
  const labelEl = host.querySelector("[data-field-label]");
  const calBtn = host.querySelector(".ar-dateinput-cal");
  const inputs = {};
  host.querySelectorAll(".ar-dateinput-seg").forEach((el) => { inputs[el.dataset.seg] = el; });

  // ── Helpers ─────────────────────────────────────────────────────────────--
  const prevSeg = (seg) => order[order.indexOf(seg) - 1] || null;
  const nextSeg = (seg) => order[order.indexOf(seg) + 1] || null;

  function focusSeg(seg, pos = "end") {
    const el = inputs[seg];
    if (!el) return;
    el.focus();
    const len = el.value.length;
    if (pos === "start") el.setSelectionRange(0, 0);
    else if (pos === "all") el.setSelectionRange(0, len);
    else el.setSelectionRange(len, len);
  }

  function syncInputs() {
    for (const seg of order) inputs[seg].value = segs[seg];
  }

  function setError(message) {
    if (errorEl) { errorEl.textContent = message || ""; errorEl.hidden = !message; }
    if (labelEl) labelEl.classList.toggle("ar-input-spec-label-error", Boolean(message));
    root.classList.toggle("ar-dateinput-error", Boolean(message));
  }

  // A note is not an error: the value is valid, it just has a consequence
  // worth saying. Neutral colour, no red label, no error ring — and it sits
  // below the field because it depends on what was typed, unlike `hint`,
  // which describes the field itself and is fixed at mount.
  function setNote(message) {
    if (!noteEl) return;
    noteEl.textContent = message || "";
    noteEl.hidden = !message;
  }

  // Recompute the committed value. `finalize` (blur / explicit validate) pads
  // single-digit day/month and is the gate — with "all three filled" — for
  // showing the inline error. Never shows mid-keystroke.
  function commit(finalize) {
    if (finalize) {
      for (const s of ["day", "month"]) {
        if (segs[s].length === 1) { segs[s] = pad(segs[s], 2); inputs[s].value = segs[s]; }
      }
    }
    const res = validateParts(segs, { min, max, required, locale });
    hidden.value = res.ok ? res.iso : "";
    const allFilled = segs.day && segs.month && segs.year.length === 4;
    setError(finalize || allFilled ? (res.ok ? "" : res.message) : "");
    if (res.iso !== lastCommitted) { lastCommitted = res.iso; onChange?.(res.iso); }
    return res;
  }

  // Has the segment received as many digits as it can usefully hold? Triggers
  // auto-advance — including early (1 digit) when no valid 2nd digit exists.
  function segComplete(seg, buf) {
    if (seg === "year") return buf.length === 4;
    if (buf.length === 2) return true;
    if (buf.length === 1) {
      if (seg === "day") return Number(buf) >= 4;    // 4–9 → no valid 2nd digit (40+ > 31)
      if (seg === "month") return Number(buf) >= 2;  // 2–9 → no valid 2nd digit (20+ > 12)
    }
    return false;
  }

  function typeDigit(seg, digit) {
    const el = inputs[seg];
    let buf = segs[seg];
    if (el._fresh) { buf = ""; el._fresh = false; }
    if (buf.length >= SEG[seg].len) buf = "";
    const candidate = buf + digit;
    // Reject a 2nd digit that would put the segment out of range (keeps the
    // first digit so the user can finish it).
    if (candidate.length === 2) {
      if (seg === "day" && (Number(candidate) === 0 || Number(candidate) > daysInMonth(segs.month, segs.year))) return;
      if (seg === "month" && (Number(candidate) === 0 || Number(candidate) > 12)) return;
    }
    segs[seg] = candidate;
    el.value = candidate;
    el.setSelectionRange(candidate.length, candidate.length);
    commit(false);
    if (segComplete(seg, candidate)) focusNext(seg);
  }

  function focusNext(seg) { const n = nextSeg(seg); if (n) focusSeg(n, "all"); }

  function incDec(seg, delta) {
    if (seg === "year") {
      const cur = segs.year ? Number(segs.year) : (new Date().getFullYear() - (delta > 0 ? 1 : 0));
      const val = Math.max(1900, Math.min(2100, cur + delta));
      segs.year = String(val);
    } else {
      const hi = seg === "month" ? 12 : daysInMonth(segs.month, segs.year);
      let val;
      if (!segs[seg]) val = delta > 0 ? 1 : hi;
      else { val = Number(segs[seg]) + delta; if (val > hi) val = 1; if (val < 1) val = hi; }
      segs[seg] = pad(val, 2);
    }
    inputs[seg].value = segs[seg];
    inputs[seg]._fresh = false;
    inputs[seg].setSelectionRange(0, segs[seg].length);
    commit(false);
  }

  // ── Segment wiring ──────────────────────────────────────────────────────--
  for (const seg of order) {
    const el = inputs[seg];
    el.addEventListener("focus", () => { el._fresh = true; el.setSelectionRange(0, el.value.length); });
    el.addEventListener("keydown", (e) => {
      if (e.metaKey || e.ctrlKey) return; // let copy/paste/etc. through
      if (/^\d$/.test(e.key)) { e.preventDefault(); typeDigit(seg, e.key); return; }
      switch (e.key) {
        case "Backspace":
          e.preventDefault();
          if (segs[seg].length > 0) {
            segs[seg] = segs[seg].slice(0, -1);
            el.value = segs[seg];
            el._fresh = false;
          } else {
            const p = prevSeg(seg);
            if (p) { segs[p] = segs[p].slice(0, -1); inputs[p].value = segs[p]; focusSeg(p, "end"); }
          }
          commit(false);
          break;
        case "ArrowLeft":
          if (el.selectionStart === 0 && el.selectionEnd === 0) { e.preventDefault(); const p = prevSeg(seg); if (p) focusSeg(p, "end"); }
          break;
        case "ArrowRight":
          if (el.selectionStart === el.value.length) { e.preventDefault(); const n = nextSeg(seg); if (n) focusSeg(n, "start"); }
          break;
        case "ArrowUp":   e.preventDefault(); incDec(seg, 1); break;
        case "ArrowDown": e.preventDefault(); incDec(seg, -1); break;
        default: break;
      }
    });
    el.addEventListener("paste", (e) => {
      const text = (e.clipboardData || window.clipboardData)?.getData("text") || "";
      const parsed = parsePastedDate(text, order);
      if (!parsed) return; // let a stray digit paste fall through to normal handling
      e.preventDefault();
      segs.day = pad(parsed.day, 2); segs.month = pad(parsed.month, 2); segs.year = pad(parsed.year, 4);
      syncInputs();
      commit(true);
      focusSeg("year", "end");
    });
  }

  // Finalize when focus leaves the whole control (segments OR calendar button).
  root.addEventListener("focusout", () => {
    setTimeout(() => { if (!root.contains(document.activeElement) && !cal.isOpen()) commit(true); }, 0);
  });

  // The whole control reads as one field: clicking the box chrome (padding,
  // separators, empty space) or the label focuses the first segment. mousedown +
  // preventDefault so focus lands on the segment, not the clicked dead area.
  root.addEventListener("mousedown", (e) => {
    if (e.target.closest(".ar-dateinput-seg, .ar-dateinput-cal, .ar-datecal")) return;
    e.preventDefault();
    focusSeg(order[0], "all");
  });
  labelEl?.addEventListener("mousedown", (e) => { e.preventDefault(); focusSeg(order[0], "all"); });

  // ── Calendar overlay ──────────────────────────────────────────────────────
  const cal = createCalendar({ root, calBtn, locale, min, max, getValue: () => hidden.value, onPick: (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m) return;
    segs.year = m[1]; segs.month = m[2]; segs.day = m[3];
    syncInputs();
    commit(true);
    focusSeg(order[0], "all");
  }});

  // ── Initial value ───────────────────────────────────────────────────────--
  function setValue(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
    if (m) { segs.year = m[1]; segs.month = m[2]; segs.day = m[3]; }
    else { segs.day = ""; segs.month = ""; segs.year = ""; }
    syncInputs();
    commit(false);
  }
  setValue(value);
  lastCommitted = hidden.value; // don't fire onChange for the initial value

  return {
    element: host,
    getValue: () => hidden.value,
    setValue,
    validate: () => commit(true),
    setNote,
    focus: () => focusSeg(order[0], "all"),
    destroy: () => cal.destroy(),
  };
}

// ── Calendar overlay (popover month grid) ─────────────────────────────────--
function createCalendar({ root, calBtn, locale, min, max, getValue, onPick }) {
  let pop = null;
  let viewY, viewM; // 0-indexed month

  const isDisabled = (iso) => (min && iso < min) || (max && iso > max);
  // The calendar's Today is the company's, as every day it picks is.
  const todayIso = () => companyToday();

  function open() {
    if (pop) return;
    const sel = getValue() || todayIso();
    const base = /^(\d{4})-(\d{2})-(\d{2})$/.exec(sel);
    const today = /^(\d{4})-(\d{2})/.exec(todayIso());
    viewY = base ? Number(base[1]) : Number(today[1]);
    viewM = base ? Number(base[2]) - 1 : Number(today[2]) - 1;
    pop = document.createElement("div");
    pop.className = "ar-datecal";
    pop.setAttribute("role", "dialog");
    pop.setAttribute("aria-label", "Choose date");
    root.appendChild(pop);
    render();
    calBtn.setAttribute("aria-expanded", "true");
    document.addEventListener("pointerdown", onOutside, true);
    document.addEventListener("keydown", onKey, true);
    // Focus the selected (or today's) day, else the first enabled day.
    const focusTarget = pop.querySelector('[aria-selected="true"]') || pop.querySelector(".ar-datecal-day:not(:disabled)");
    focusTarget?.focus();
  }

  function close(returnFocus = true) {
    if (!pop) return;
    document.removeEventListener("pointerdown", onOutside, true);
    document.removeEventListener("keydown", onKey, true);
    pop.remove(); pop = null;
    calBtn.setAttribute("aria-expanded", "false");
    if (returnFocus) calBtn.focus();
  }

  function onOutside(e) { if (pop && !pop.contains(e.target) && e.target !== calBtn && !calBtn.contains(e.target)) close(false); }

  function onKey(e) {
    if (!pop) return;
    if (e.key === "Escape") { e.preventDefault(); close(true); return; }
    const focusedDay = pop.querySelector(".ar-datecal-day:focus");
    if (!focusedDay) return;
    const iso = focusedDay.dataset.date;
    const move = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[e.key];
    if (move != null) {
      e.preventDefault();
      const d = new Date(iso + "T00:00:00");
      d.setDate(d.getDate() + move);
      const targetIso = `${pad(d.getFullYear(), 4)}-${pad(d.getMonth() + 1, 2)}-${pad(d.getDate(), 2)}`;
      if (d.getFullYear() !== viewY || d.getMonth() !== viewM) { viewY = d.getFullYear(); viewM = d.getMonth(); render(); }
      const target = pop.querySelector(`[data-date="${targetIso}"]`);
      if (target && !target.disabled) target.focus();
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!focusedDay.disabled) { onPick(iso); close(false); }
    }
  }

  function shiftMonth(delta) {
    viewM += delta;
    if (viewM < 0) { viewM = 11; viewY--; }
    if (viewM > 11) { viewM = 0; viewY++; }
    render();
  }

  function render() {
    const monthLabel = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(new Date(viewY, viewM, 1));
    // Weekday headers (Mon-first), localised short names.
    const weekFmt = new Intl.DateTimeFormat(locale, { weekday: "short" });
    const headers = [];
    for (let i = 0; i < 7; i++) headers.push(weekFmt.format(new Date(2024, 0, 1 + i))); // Jan 1 2024 = Monday
    const first = new Date(viewY, viewM, 1);
    const offset = (first.getDay() + 6) % 7; // days before the 1st, Mon-first
    const days = new Date(viewY, viewM + 1, 0).getDate();
    const selected = getValue();
    const today = todayIso();

    let cells = "";
    for (let i = 0; i < offset; i++) cells += `<span class="ar-datecal-pad" aria-hidden="true"></span>`;
    for (let d = 1; d <= days; d++) {
      const iso = `${pad(viewY, 4)}-${pad(viewM + 1, 2)}-${pad(d, 2)}`;
      const disabled = isDisabled(iso);
      const isSel = iso === selected;
      const isToday = iso === today;
      cells += `<button type="button" class="ar-datecal-day${isSel ? " is-selected" : ""}${isToday ? " is-today" : ""}"
                  data-date="${iso}"${disabled ? " disabled" : ""}${isSel ? ' aria-selected="true"' : ""}
                  aria-label="${formatHuman(iso, locale)}" tabindex="-1">${d}</button>`;
    }

    pop.innerHTML = `
      <div class="ar-datecal-head">
        <button type="button" class="ar-datecal-nav" data-nav="-1" aria-label="Previous month">‹</button>
        <span class="ar-datecal-title" aria-live="polite">${monthLabel}</span>
        <button type="button" class="ar-datecal-nav" data-nav="1" aria-label="Next month">›</button>
      </div>
      <div class="ar-datecal-grid ar-datecal-weekdays" aria-hidden="true">${headers.map((h) => `<span class="ar-datecal-wd">${h}</span>`).join("")}</div>
      <div class="ar-datecal-grid ar-datecal-days" role="grid">${cells}</div>
      <div class="ar-datecal-foot">
        <button type="button" class="ar-datecal-today">Today</button>
      </div>`;

    pop.querySelectorAll("[data-nav]").forEach((b) => b.addEventListener("click", () => shiftMonth(Number(b.dataset.nav))));
    pop.querySelectorAll(".ar-datecal-day").forEach((b) => {
      b.tabIndex = 0;
      b.addEventListener("click", () => { if (!b.disabled) { onPick(b.dataset.date); close(true); } });
    });
    const todayBtn = pop.querySelector(".ar-datecal-today");
    todayBtn.disabled = isDisabled(today);
    todayBtn.addEventListener("click", () => { if (!isDisabled(today)) { onPick(today); close(true); } });
  }

  calBtn.addEventListener("click", () => (pop ? close(true) : open()));

  return { isOpen: () => Boolean(pop), destroy: () => close(false) };
}
